import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { personLabel } from '../../../../shared/contracts/approval-trail.js';
import {
  ledgerQuery,
  type LedgerAmounts,
  type LedgerSummary,
  type LedgerSummaryRow,
} from '../../../../shared/contracts/vendor-ledger.js';
import { DataGrid, type DataGridColumn } from '../../components/DataGrid.js';
import { LedgerPeriodForm, useBalance } from '../../components/LedgerStatement.js';
import { PageHeader } from '../../../../shared/components/PageHeader.js';
import { Card } from '../../../../shared/components/Card.js';
import { Alert } from '../../../../shared/components/Alert.js';
import { EmptyState } from '../../../../shared/components/EmptyState.js';
import { StatCard } from '../../../../shared/components/StatCard.js';

type AmountKey = keyof LedgerAmounts;
const AMOUNTS: readonly AmountKey[] = ['opening', 'billed', 'tds', 'deductions', 'paid', 'reversed', 'closing'];

/**
 * The Sales Accounts ledger (owner, 29-30 Oct): every vendor's account for the
 * period - opening, bills booked, TDS, other deductions, paid, reversed and
 * closing - with totals that are the sum of the vendors' ledgers, each vendor
 * opening its own ledger; and the bills outstanding at the end of the period
 * aged from their bill date into the tenant's day ranges.
 */
export function SalesAccountsLedgerPage(): ReactNode {
  const { t } = useTranslation();
  const { day } = useBusinessDay();
  const { money } = useBusinessNumber();
  const balance = useBalance();
  const { token } = useAuth();
  const [period, setPeriod] = useState({ from: '', to: '' });
  const [data, setData] = useState<LedgerSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    apiRequest<LedgerSummary>(`/v1/payments/ledger${ledgerQuery(period.from, period.to)}`, { token })
      .then(setData)
      .catch((e) => setError((e as Error).message));
  }, [period, token]);
  useEffect(() => { load(); }, [load]);

  const vendorLink = (v: Pick<LedgerSummaryRow, 'vendorId' | 'vendorName' | 'vendorCode'>) => (
    <Link to={`/finance/ledger/vendor/${v.vendorId}${ledgerQuery(data?.from ?? '', data?.to ?? '')}`}>
      {personLabel(v.vendorName, v.vendorCode) ?? v.vendorId}
    </Link>
  );
  const amountColumn = (k: AmountKey): DataGridColumn<LedgerSummaryRow> => ({
    key: k,
    header: t(`ledger.col.${k}`),
    value: (r) => r[k],
    render: (r) => (k === 'opening' || k === 'closing' ? balance(r[k]) : money(r[k])),
    numeric: true,
  });
  const columns: DataGridColumn<LedgerSummaryRow>[] = [
    { key: 'vendor', header: t('ledger.col.vendor'), value: (r) => personLabel(r.vendorName, r.vendorCode) ?? '', render: vendorLink },
    ...AMOUNTS.map(amountColumn),
  ];

  const bucketHeader = (b: { from: number; to: number | null }) =>
    b.to === null ? t('ledger.ageing.over', { days: String(b.from - 1) }) : t('ledger.ageing.bucket', { from: String(b.from), to: String(b.to) });
  const cell = { padding: '12px 14px', textAlign: 'right', borderBottom: '1px solid var(--color-border-default)' } as const;
  const head = { ...cell, fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-muted)', background: 'var(--color-bg-muted)' } as const;
  const sectionTitle = { margin: '0 0 4px', fontSize: 18, fontWeight: 600 } as const;
  const muted = { margin: '0 0 16px', color: 'var(--color-text-muted)' } as const;

  return (
    <div className="tl-page" style={{ padding: 24 }}>
      <PageHeader title={t('ledger.sa.title')} subtitle={t('ledger.sa.note')} />
      <Card>
        <LedgerPeriodForm from={data?.from ?? period.from} to={data?.to ?? period.to} onShow={(from, to) => setPeriod({ from, to })} />
        {data && <p style={{ margin: '12px 0 0', color: 'var(--color-text-muted)' }}>{t('ledger.period', { from: day(data.from), to: day(data.to) })}</p>}
      </Card>
      {error && <Alert tone="danger">{error}</Alert>}
      {!data && !error && <p style={{ color: 'var(--color-text-muted)' }}>{t('ledger.loading')}</p>}
      {data && (
        <>
          <section aria-label={t('ledger.totals')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, margin: '20px 0' }}>
            {AMOUNTS.map((k) => (
              <StatCard
                key={k}
                data-testid={`total-${k}`}
                label={t(`ledger.col.${k}`)}
                value={k === 'opening' || k === 'closing' ? balance(data.totals[k]) : money(data.totals[k])}
              />
            ))}
          </section>
          <Card>
            <DataGrid
              caption={t('ledger.vendors')}
              columns={columns}
              rows={data.vendors}
              getRowId={(r) => r.vendorId}
              rowAriaLabel={(r) => `vendor-${r.vendorCode ?? r.vendorId}`}
              pageSize={25}
              exportFileName={`sales-accounts-ledger-${data.from}-${data.to}.csv`}
              emptyMessage={t('ledger.noVendors')}
            />
          </Card>

          <div style={{ marginTop: 24 }}>
            <Card>
              <h2 style={sectionTitle}>{t('ledger.ageing.title', { date: day(data.ageing.asOn) })}</h2>
              <p style={muted}>{t('ledger.ageing.note')}</p>
              {data.ageing.vendors.length === 0 ? (
                <div data-testid="ageing-empty"><EmptyState title={t('ledger.ageing.empty')} /></div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table aria-label={t('ledger.ageing.caption')} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr>
                        <th style={{ ...head, textAlign: 'left' }}>{t('ledger.col.vendor')}</th>
                        {data.ageing.buckets.map((b) => <th key={b.from} style={head}>{bucketHeader(b)}</th>)}
                        <th style={head}>{t('ledger.ageing.total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.ageing.vendors.map((v) => (
                        <tr key={v.vendorId}>
                          <td style={{ ...cell, textAlign: 'left' }}>{vendorLink(v)}</td>
                          {v.amounts.map((a, i) => <td key={i} style={cell}>{a ? money(a) : ''}</td>)}
                          <td style={{ ...cell, fontWeight: 600 }}>{money(v.total)}</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 600, background: 'var(--color-bg-muted)' }}>
                        <td style={{ ...cell, textAlign: 'left' }}>{t('ledger.totals')}</td>
                        {data.ageing.totals.map((a, i) => <td key={i} style={cell}>{money(a)}</td>)}
                        <td style={cell} data-testid="ageing-total">{money(data.ageing.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
